import {
  ComparisonMode,
  DateRangeValue,
  RangeGranularity,
  RangePreset,
} from './date-range-comparison-picker.types';

export interface FiscalYearStart {
  month: number; // 1-12
  day: number; // 1-31
}

export const DEFAULT_FISCAL_YEAR_START: FiscalYearStart = { month: 1, day: 1 };

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}

export function addYears(d: Date, n: number): Date {
  return addMonths(d, n * 12);
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

export function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}

export function endOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return endOfDay(new Date(d.getFullYear(), q * 3 + 3, 0));
}

export function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

export function endOfYear(d: Date): Date {
  return endOfDay(new Date(d.getFullYear(), 11, 31));
}

export function startOfFiscalYear(ref: Date, start: FiscalYearStart): Date {
  const candidate = new Date(ref.getFullYear(), start.month - 1, start.day);
  return ref < candidate
    ? new Date(ref.getFullYear() - 1, start.month - 1, start.day)
    : candidate;
}

export function endOfFiscalYear(ref: Date, start: FiscalYearStart): Date {
  const s = startOfFiscalYear(ref, start);
  return endOfDay(addDays(addYears(s, 1), -1));
}

export function rangeForYear(year: number): DateRangeValue {
  return {
    from: new Date(year, 0, 1),
    to: endOfDay(new Date(year, 11, 31)),
  };
}

/** Builds the fiscal-year window for the supplied end-year label. `start`
    defines the first day of the fiscal year; the returned end is the final
    moment before the next fiscal year starts. */
export function rangeForFiscalYear(
  fyEndYear: number,
  start: FiscalYearStart
): DateRangeValue {
  const from = new Date(fyEndYear - 1, start.month - 1, start.day);
  const to = endOfDay(addDays(addYears(from, 1), -1));
  return { from, to };
}

export function isFiscalYearConfigured(start: FiscalYearStart): boolean {
  return start.month !== 1 || start.day !== 1;
}

export function rangeForQuarter(year: number, quarter: 1 | 2 | 3 | 4): DateRangeValue {
  const startMonth = (quarter - 1) * 3;
  return {
    from: new Date(year, startMonth, 1),
    to: endOfDay(new Date(year, startMonth + 3, 0)),
  };
}

export function rangeForMonth(year: number, month: number): DateRangeValue {
  return {
    from: new Date(year, month, 1),
    to: endOfDay(new Date(year, month + 1, 0)),
  };
}

export function rangeForPreset(
  preset: RangePreset,
  ref: Date = new Date(),
  fiscal: FiscalYearStart = DEFAULT_FISCAL_YEAR_START
): DateRangeValue {
  const today = startOfDay(ref);
  switch (preset) {
    case 'yearToDate':
      return { from: startOfYear(today), to: endOfDay(today) };
    case 'today':
      return { from: today, to: endOfDay(today) };
    case 'yesterday': {
      const yesterday = addDays(today, -1);
      return { from: yesterday, to: endOfDay(yesterday) };
    }
    case 'thisWeek': {
      const dow = today.getDay();
      const mondayOffset = dow === 0 ? -6 : 1 - dow;
      const monday = addDays(today, mondayOffset);
      return { from: monday, to: endOfDay(today) };
    }
    case 'thisMonth':
      return { from: startOfMonth(today), to: endOfMonth(today) };
    case 'fiscalYear':
      return {
        from: startOfFiscalYear(today, fiscal),
        to: endOfFiscalYear(today, fiscal),
      };
    case 'allTime':
      return {
        from: new Date(1970, 0, 1),
        to: endOfDay(new Date(today.getFullYear() + 10, 11, 31)),
      };
    case 'thisQuarter':
      return { from: startOfQuarter(today), to: endOfQuarter(today) };
    case 'lastQuarter': {
      const prevQuarter = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      return { from: startOfQuarter(prevQuarter), to: endOfQuarter(prevQuarter) };
    }
    case 'thisYear':
      return { from: startOfYear(today), to: endOfYear(today) };
    case 'lastYear': {
      const prevYear = new Date(
        today.getFullYear() - 1,
        today.getMonth(),
        today.getDate()
      );
      return { from: startOfYear(prevYear), to: endOfYear(prevYear) };
    }
    case 'last30Days':
      return { from: addDays(today, -29), to: endOfDay(today) };
    case 'last90Days':
      return { from: addDays(today, -89), to: endOfDay(today) };
  }
}

export function computeComparison(
  range: DateRangeValue,
  mode: ComparisonMode,
  custom: DateRangeValue | null = null
): DateRangeValue | null {
  switch (mode) {
    case 'none':
      return null;
    case 'previousYear':
      return { from: addYears(range.from, -1), to: addYears(range.to, -1) };
    case 'previousPeriod': {
      // Day-aligned: round to whole days so a 1-day range (today) maps
      // to exactly 1 day back (yesterday), not ~2 due to the 23:59:59.999
      // end-of-day offset.
      const lengthDays = Math.max(
        1,
        Math.round(
          (endOfDay(range.to).getTime() - startOfDay(range.from).getTime()) / 86400000
        )
      );
      const to = addDays(startOfDay(range.from), -1);
      const from = addDays(to, -(lengthDays - 1));
      return { from: startOfDay(from), to: endOfDay(to) };
    }
    case 'custom':
      return custom;
  }
}

export function shiftRange(
  range: DateRangeValue,
  granularity: RangeGranularity,
  direction: -1 | 1
): DateRangeValue {
  switch (granularity) {
    case 'year':
    case 'fiscalYear':
      return { from: addYears(range.from, direction), to: addYears(range.to, direction) };
    case 'quarter':
      return {
        from: addMonths(range.from, direction * 3),
        to: endOfDay(addMonths(range.to, direction * 3)),
      };
    case 'month': {
      const from = addMonths(range.from, direction);
      return { from, to: endOfMonth(from) };
    }
    case 'custom': {
      const lengthDays = Math.round(
        (range.to.getTime() - range.from.getTime()) / 86400000
      );
      const days = direction * (lengthDays + 1);
      return { from: addDays(range.from, days), to: addDays(range.to, days) };
    }
  }
}

export function inferGranularity(
  range: DateRangeValue,
  fiscal: FiscalYearStart = DEFAULT_FISCAL_YEAR_START
): RangeGranularity {
  const { from, to } = range;
  const isStartOfMonth = from.getDate() === 1;
  const isEndOfMonth = endOfMonth(to).toDateString() === to.toDateString();

  // Fiscal year detection — only when the org has a non-default fiscal
  // start. Matches a range starting on `fiscal.month/day` and ending one
  // year later minus a day. Checked before the generic year branch so a
  // Sep-Aug range doesn't get mislabeled as 'custom'.
  if (isFiscalYearConfigured(fiscal)) {
    const startsOnFiscal =
      from.getMonth() === fiscal.month - 1 && from.getDate() === fiscal.day;
    const expectedEnd = endOfDay(addDays(addYears(from, 1), -1));
    const endsOnFiscalEnd = expectedEnd.toDateString() === to.toDateString();
    if (startsOnFiscal && endsOnFiscalEnd) return 'fiscalYear';
  }

  if (!isStartOfMonth || !isEndOfMonth) return 'custom';

  const startsJan = from.getMonth() === 0;
  const endsDec = to.getMonth() === 11;
  if (startsJan && endsDec && from.getFullYear() === to.getFullYear()) return 'year';

  const sameQuarter =
    Math.floor(from.getMonth() / 3) === Math.floor(to.getMonth() / 3) &&
    from.getMonth() % 3 === 0 &&
    to.getMonth() % 3 === 2 &&
    from.getFullYear() === to.getFullYear();
  if (sameQuarter) return 'quarter';

  if (from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear())
    return 'month';

  return 'custom';
}

const MONTHS_DE = [
  'Jan',
  'Feb',
  'Mär',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Dez',
] as const;

export function monthLabelDe(month: number): string {
  return MONTHS_DE[month] ?? '';
}

export function formatDateDe(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function formatRangeDe(range: DateRangeValue, compact = false): string {
  // Drop the redundant year on the `from` side when both ends are in the
  // same year — `01.01. - 06.05.2026` reads cleaner than the doubled form.
  // In compact mode, also abbreviate the year to a 2-digit form prefixed
  // with a typographic apostrophe (e.g. `'26`).
  const toLabel = compact ? formatDateDeShortYear(range.to) : formatDateDe(range.to);
  if (range.from.getFullYear() === range.to.getFullYear()) {
    const dd = String(range.from.getDate()).padStart(2, '0');
    const mm = String(range.from.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}. - ${toLabel}`;
  }
  const fromLabel = compact
    ? formatDateDeShortYear(range.from)
    : formatDateDe(range.from);
  return `${fromLabel} - ${toLabel}`;
}

function formatDateDeShortYear(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.‘${yy}`;
}

export function formatTriggerLabel(
  range: DateRangeValue,
  comparison: DateRangeValue | null,
  _granularity: RangeGranularity,
  fiscal: FiscalYearStart = DEFAULT_FISCAL_YEAR_START,
  compact = false
): string {
  // Always derive the label from the range shape itself, not the picker's
  // declared granularity. The picker may report `month` while the range
  // actually spans multiple months (shift-extend selection inside the
  // month tab), and the old version used `range.from` blindly which
  // produced misleading labels like "Okt 2025" for an Oct-Dec range.
  const primary = compactRangeLabel(range, fiscal, compact);
  // In compact mode, the `vs <range>` suffix is dropped — it doubles the
  // label width and the comparison year is usually implicit from the
  // configured comparison mode anyway.
  if (!comparison || compact) return primary;
  return `${primary} vs ${compactRangeLabel(comparison, fiscal, compact)}`;
}

function compactRangeLabel(
  range: DateRangeValue,
  fiscal: FiscalYearStart = DEFAULT_FISCAL_YEAR_START,
  compact = false
): string {
  const inferred = inferGranularity(range, fiscal);
  if (inferred === 'fiscalYear') {
    const endYear = range.to.getFullYear();
    const startYear = range.from.getFullYear();
    return `GJ ${String(startYear).slice(-2)}/${String(endYear).slice(-2)}`;
  }
  if (inferred === 'year' && range.from.getFullYear() === range.to.getFullYear()) {
    return compact
      ? `‘${String(range.from.getFullYear()).slice(-2)}`
      : String(range.from.getFullYear());
  }
  if (inferred === 'quarter' && range.from.getFullYear() === range.to.getFullYear()) {
    const q = Math.floor(range.from.getMonth() / 3) + 1;
    const year = compact
      ? `‘${String(range.from.getFullYear()).slice(-2)}`
      : String(range.from.getFullYear());
    return `Q${q} ${year}`;
  }
  if (inferred === 'month' && range.from.getFullYear() === range.to.getFullYear()) {
    const year = compact
      ? `‘${String(range.from.getFullYear()).slice(-2)}`
      : String(range.from.getFullYear());
    return `${monthLabelDe(range.from.getMonth())} ${year}`;
  }
  return formatRangeDe(range, compact);
}

export function rangesEqual(a: DateRangeValue | null, b: DateRangeValue | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime();
}

export function toIsoDate(d: Date | null): string | null {
  if (!d) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function fromIsoDate(s: string | null): Date | null {
  if (!s) return null;
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
